import React, { useEffect, useState } from "react";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import RootState from "../../../redux/states/root.state";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { assignRole, fetchRole } from "../../../redux/actions/roles.actions";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import { clearError } from "../../../redux/reducers/roleUsers.reducer";

const ApplyRolesModal: React.FC<ModalProps> = ({open, setOpen, selected, selection}) => {
  const [submitted, setSubmitted] = useState(false);
  const [showCorporateWarning, setShowCorporateWarning] = useState(false);

  const dispatch: AppDispatch = useDispatch();
  const {loading, error} = useSelector((state: RootState) => state.roleUsers);
  const {data:role, loading: roleLoading} = useSelector((state: RootState) => state.role);
  const users = useSelector((state: RootState) => state.users.data);
  
  useEffect(() => {
    if (selected) {
      dispatch(fetchRole(selected))
    }
  }, [selected, dispatch]);

  useEffect(() => {
    // Check if any selected user has corporate role
    const hasCorporateUser = selection?.some(user => user?.roles[0]?.name === 'corporate');

    if (hasCorporateUser && role?.name !== 'corporate') {
      setShowCorporateWarning(true);
    } else {
      setShowCorporateWarning(false);
    }
  }, [selection, role, users]);

  const handleClose = () => {
    dispatch(clearError());
    setSubmitted(false);
    setShowCorporateWarning(false);
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (showCorporateWarning) {
        setShowCorporateWarning(false);
    }
    await dispatch(assignRole({role_id: selected, users: selection ?? []}));
    setSubmitted(true);
  };

  const isLoading = loading || roleLoading;
  const failed = Boolean(error ?? !selection) && submitted;
  const count = selection?.length;

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size="sm"
      busy={loading}
      tone={isLoading ? undefined : failed ? "error" : submitted ? "success" : showCorporateWarning ? "warning" : undefined}
      title={
        isLoading ? "Loading…"
        : failed ? "An error occurred"
        : submitted ? "Roles applied"
        : showCorporateWarning ? "Corporate role change"
        : <>Give <strong>{role?.name}</strong> role to <strong>{count}</strong> {count === 1 ? 'user' : 'users'}?</>
      }
      actions={isLoading ? undefined : submitted ?
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
        : showCorporateWarning ?
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-danger" onClick={handleConfirm}>Continue</button>
        </>
        :
        <>
          <button className="btn" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm}>Confirm</button>
        </>
      }
    >
      {isLoading ?
        <ModalLoading/>
        : failed ?
        <p>{error ?? `No users selected.`}</p>
        : submitted ?
        <p>Roles applied to the selected user(s).</p>
        : showCorporateWarning ?
        <>
          {count === 1 ?
            <p>The selected user has a corporate role. Changing their role to <strong>{role?.name}</strong> will unlink them as a corporation from all their dealerships.</p>
            :
            <p>At least one of the <strong>{count}</strong> selected users has a corporate role. Changing their role to <strong>{role?.name}</strong> will unlink them as a corporation from all their dealerships.</p>
          }
          <p>Do you want to continue?</p>
        </>
        : undefined
      }
    </ModalShell>
  );
};

export default ApplyRolesModal;